import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TabsList } from './tabs';

type ScrollableTabsListProps = React.ComponentPropsWithoutRef<typeof TabsList> & {
  /** Classes for the outer wrapper (put margins here, not on the list). */
  containerClassName?: string;
};

const arrowClass =
  'absolute inset-y-0 z-10 flex items-center text-gray-500 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * A TabsList that never pushes the page sideways: when the tabs are wider
 * than their container they scroll horizontally (scrollbar hidden) and
 * chevron buttons appear on the overflowing side(s). The active tab is
 * scrolled into view on mount and whenever it changes.
 */
export const ScrollableTabsList = ({ className, containerClassName, children, ...props }: ScrollableTabsListProps) => {
  const listRef = React.useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = React.useState({ left: false, right: false });

  const update = React.useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setCanScroll({
      left: el.scrollLeft > 1,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  React.useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const revealActive = () =>
      el.querySelector<HTMLElement>('[data-state="active"]')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });

    revealActive();
    update();
    el.addEventListener('scroll', update, { passive: true });
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    resize?.observe(el);
    const activeChange = typeof MutationObserver !== 'undefined' ? new MutationObserver(revealActive) : null;
    activeChange?.observe(el, { subtree: true, attributeFilter: ['data-state'] });

    return () => {
      el.removeEventListener('scroll', update);
      resize?.disconnect();
      activeChange?.disconnect();
    };
  }, [update]);

  const scrollByPage = (direction: -1 | 1) => {
    const el = listRef.current;
    el?.scrollBy({ left: direction * el.clientWidth * 0.7, behavior: 'smooth' });
  };

  return (
    <div className={cn('relative min-w-0', containerClassName)}>
      {canScroll.left && (
        <button
          type="button"
          aria-label="Scroll tabs left"
          onClick={() => scrollByPage(-1)}
          className={cn(arrowClass, 'left-0 bg-gradient-to-r from-background via-background to-transparent pl-1 pr-6')}
        >
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </button>
      )}
      <TabsList
        ref={listRef}
        className={cn(
          'flex-nowrap overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0',
          className,
        )}
        {...props}
      >
        {children}
      </TabsList>
      {canScroll.right && (
        <button
          type="button"
          aria-label="Scroll tabs right"
          onClick={() => scrollByPage(1)}
          className={cn(arrowClass, 'right-0 bg-gradient-to-l from-background via-background to-transparent pl-6 pr-1')}
        >
          <ChevronRight className="h-5 w-5" aria-hidden />
        </button>
      )}
    </div>
  );
};
